// /settings (admin): tasks, stations, shelves and their levels, rule
// switches, prompts, board slides, the planner's schedule, the buffer and
// score weights, and roles. Every save asks for a reason; the API audits it.
// Facts are rules; how much to keep on the shelves is the ratio and the score,
// so there is no "add a buffer rule" here on purpose.
import { useIonToast } from "@ionic/react";
import { useEffect, useState } from "react";
import type { PersonRow, Role, SettingRow, ShelfRow, TaskRow } from "../lib/types";
import type { StationRow } from "../lib/types";
import { BuildFlow, ShelfGauge } from "../design";
import { announce, can, useApi, useMe } from "../api/live";
import { ApiError, put } from "../api/client";
import { paths } from "../api/paths";
import { stamp } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { Page } from "../shell/Page";
import { ErrorNote, Loading, useReason } from "../shell/ui";

const TABS = { register: "Tasks and stations", shelves: "Shelves", rules: "Rules", planner: "Planner", prompts: "Prompts", board: "Board", roles: "Roles" } as const;
type Tab = keyof typeof TABS;

type Rule = { code: string; name: string; says?: string; kind: "block" | "warn" | "advisory"; enabled: boolean; setting?: string | null };
type Prompt = { id: string; on: boolean; at: string; days: string[]; text: string };
type Slide = { id: string; seconds: number };

/** PUT with a reason; toasts the outcome. */
function useSave() {
  const ask = useReason();
  const [present] = useIonToast();
  return async (what: string, path: string, body: Record<string, unknown>, entity: string) => {
    const reason = await ask(`Save ${what}`, "Settings changes are audited. Why this change?");
    if (!reason) return false;
    try {
      await put(path, { ...body, reason });
      present({ message: `${what[0].toUpperCase()}${what.slice(1)} saved`, duration: 2000, color: "success" });
      announce([entity]);
      return true;
    } catch (e) { present({ message: (e as ApiError).message, duration: 4000, color: "danger" }); return false; }
  };
}

export function SettingsPage() {
  const { me } = useMe();
  const [tab, setTab] = useState<Tab>("register");
  const settings = useApi<SettingRow[]>(paths.settings(), ["setting"]);
  if (!can(me, "admin")) return <Page title="Settings"><p className="empty">Settings are for admins. Ask Andrea or James.</p></Page>;
  const value = <T,>(key: string, fallback: T): T => (settings.data?.find((s) => s.key === key)?.value as T) ?? fallback;
  const updated = (key: string) => settings.data?.find((s) => s.key === key)?.updatedAt;
  return (
    <Page title="Settings">
      <div className="tabs" role="tablist">
        {(Object.keys(TABS) as Tab[]).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn btn--sm${tab === t ? " btn--primary" : ""}`} onClick={() => setTab(t)}>{TABS[t]}</button>
        ))}
      </div>
      {!settings.data ? (settings.error ? <ErrorNote error={settings.error} retry={settings.reload} /> : <Loading />) : (
        tab === "register" ? <RegisterTab />
          : tab === "shelves" ? <ShelvesTab />
          : tab === "rules" ? <RulesTab rules={value<Rule[]>("rules", [])} updated={updated("rules")} />
          : tab === "planner" ? <PlannerTab schedule={value<Record<string, string>>("schedule", {})} buffer={value<Record<string, number>>("buffer", {})} weights={value<Record<string, number>>("weights", {})} />
          : tab === "prompts" ? <PromptsTab prompts={value<Prompt[]>("prompts", [])} />
          : tab === "board" ? <BoardTab slides={value<Slide[]>("board.slides", [])} />
          : <RolesTab />
      )}
    </Page>
  );
}

function RegisterTab() {
  const { tasks } = useTasks();
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["shelf", "stock"]);
  const stations = useApi<StationRow[]>(paths.stations(), ["station"]);
  const save = useSave();
  const [sizes, setSizes] = useState<Record<string, string>>({});
  return (
    <div className="stack">
      <section className="card">
        <div className="card__head"><h2>The build flow</h2><span className="small muted">Drawn from the tasks and shelves below</span></div>
        {shelves.data ? <BuildFlow tasks={tasks} shelves={shelves.data} /> : <Loading />}
      </section>
      <section className="card scroll-x">
        <div className="card__head"><h2>Tasks</h2><span className="small muted">A change here makes a new register revision</span></div>
        <table className="table">
          <thead><tr><th>Task</th><th>Group</th><th>Steps</th><th className="num">Units a session</th><th /></tr></thead>
          <tbody>{tasks.map((t: TaskRow) => (
            <tr key={t.code}><td><b>{t.code}</b> {t.name}</td><td>{t.group}</td><td className="small">{t.steps.map((s) => s.code).join(" → ")}</td>
              <td className="num"><input className="input input--sm input--num" inputMode="numeric" aria-label={`${t.code} units a session`} value={sizes[t.code] ?? String(t.sessionQty)} onChange={(e) => setSizes({ ...sizes, [t.code]: e.target.value.replace(/\D/g, "") })} /></td>
              <td>{sizes[t.code] != null && Number(sizes[t.code]) !== t.sessionQty ? <button type="button" className="btn btn--sm btn--primary" onClick={() => save(`${t.code}'s session size`, `/api/tasks/${encodeURIComponent(t.code)}`, { sessionQty: Number(sizes[t.code]) }, "task")}>Save</button> : null}</td></tr>
          ))}</tbody>
        </table>
      </section>
      <section className="card scroll-x">
        <div className="card__head"><h2>Stations</h2><span className="small muted">Named so plan cells and e-paper tags can point at them</span></div>
        {stations.data ? (
          <table className="table"><thead><tr><th>Station</th><th>Floor</th><th>Steps it hosts</th><th>Tag</th></tr></thead>
            <tbody>{stations.data.map((s) => <tr key={s.id}><td><b>{s.name}</b></td><td>{s.floor}</td><td>{s.steps.join(", ")}</td><td>{s.tag ? <code>{s.tag}</code> : "—"}</td></tr>)}</tbody></table>
        ) : <Loading />}
      </section>
    </div>
  );
}

function ShelvesTab() {
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["shelf", "stock"]);
  const save = useSave();
  const [edits, setEdits] = useState<Record<string, Partial<Record<"critical" | "min" | "recommended", string>>>>({});
  if (!shelves.data) return <Loading />;
  const field = (s: ShelfRow, k: "critical" | "min" | "recommended") => (
    <input className="input input--sm input--num" inputMode="numeric" aria-label={`${s.code} ${k}`} value={edits[s.code]?.[k] ?? (s[k] ?? "")}
      onChange={(e) => setEdits({ ...edits, [s.code]: { ...edits[s.code], [k]: e.target.value.replace(/\D/g, "") } })} />
  );
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>Shelves and their levels</h2><span className="small muted">Critical, Min and Recommended. The ratio does the rest.</span></div>
      <table className="table">
        <thead><tr><th>Shelf</th><th className="num">Critical</th><th className="num">Min</th><th className="num">Recommended</th><th>Now</th><th /></tr></thead>
        <tbody>{shelves.data.filter((s) => !s.isOutput && s.countMode === "number").map((s) => {
          const e = edits[s.code];
          return (
            <tr key={s.code}><td><b>{s.code}</b> {s.name}<div className="small muted">{s.line} · {s.floor}</div></td>
              <td className="num">{field(s, "critical")}</td><td className="num">{field(s, "min")}</td><td className="num">{field(s, "recommended")}</td>
              <td style={{ minWidth: 200 }}><ShelfGauge shelf={s} variant="compact" /></td>
              <td>{e ? <button type="button" className="btn btn--sm btn--primary" onClick={async () => {
                const body = Object.fromEntries(Object.entries(e).map(([k, v]) => [k, v === "" ? null : Number(v)]));
                if (await save(`${s.code}'s levels`, paths.shelf(s.code), body, "shelf")) setEdits(({ [s.code]: _, ...rest }) => rest);
              }}>Save</button> : null}</td></tr>
          );
        })}</tbody>
      </table>
    </section>
  );
}

function RulesTab({ rules, updated }: { rules: Rule[]; updated?: string }) {
  const save = useSave();
  const [draft, setDraft] = useState(rules);
  useEffect(() => setDraft(rules), [rules]);
  const changed = JSON.stringify(draft) !== JSON.stringify(rules);
  return (
    <section className="card">
      <div className="card__head"><h2>Rule switches</h2><span className="small muted">Rules are code; their switches are data{updated ? ` · changed ${stamp(updated)}` : ""}</span></div>
      {draft.map((r, i) => (
        <div key={r.code} className="settings-row">
          <label className="switch"><input type="checkbox" checked={r.enabled} aria-label={`${r.code} on`} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} /><span /></label>
          <span className="settings-row__k"><b>{r.code}</b> {r.name}<small>{r.says}</small></span>
          <select className="select select--inline" aria-label={`${r.code} type`} value={r.kind} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, kind: e.target.value as Rule["kind"] } : x)))}>
            <option value="block">block</option><option value="warn">warn</option><option value="advisory">advisory</option>
          </select>
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={!changed} onClick={() => save("the rule switches", paths.setting("rules"), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function NumberObject({ title, hint, settingKey, value, labels }: { title: string; hint: string; settingKey: string; value: Record<string, number | string>; labels: Record<string, string> }) {
  const save = useSave();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const changed = JSON.stringify(draft) !== JSON.stringify(value);
  return (
    <section className="card">
      <div className="card__head"><h2>{title}</h2><span className="small muted">{hint}</span></div>
      {Object.entries(draft).map(([k, v]) => (
        <div key={k} className="settings-row">
          <span className="settings-row__k">{labels[k] ?? k}</span>
          <input className="input input--sm input--num" style={{ width: typeof v === "number" ? 96 : 200 }} aria-label={labels[k] ?? k} value={String(v)}
            onChange={(e) => setDraft({ ...draft, [k]: typeof v === "number" ? (e.target.value === "" ? 0 : Number(e.target.value)) : e.target.value })} inputMode={typeof v === "number" ? "decimal" : undefined} />
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={!changed} onClick={() => save(title.toLowerCase(), paths.setting(settingKey), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function PlannerTab({ schedule, buffer, weights }: { schedule: Record<string, string>; buffer: Record<string, number>; weights: Record<string, number> }) {
  return (
    <div className="grid-2">
      <NumberObject title="The planner's schedule" hint="Europe/London time; no deploy needed" settingKey="schedule" value={schedule}
        labels={{ draft: "Next week's draft", autoApprove: "An untouched draft is approved", proposal: "Proposal from the latest count", slotLock: "A half-day locks" }} />
      <NumberObject title="The buffer (the ratio)" hint="One control law per shelf" settingKey="buffer" value={buffer}
        labels={{ shareWhenLow: "Share of feeder output kept at or below Min", drawWhenHigh: "Above Recommended: draw down per day (0 holds)" }} />
      <NumberObject title="Score weights" hint="How plans are ranked" settingKey="weights" value={weights}
        labels={{ devices: "Per device built", swing: "Per device of day-to-day swing", shelfDistance: "Per unit away from a shelf's path", changedCells: "Per cell changed from the approved plan", sameJobRunning: "Same job two days running", anchorLearners: "Extra learner on one anchor", floatHalf: "Empty half-day of someone who's in" }} />
    </div>
  );
}

function PromptsTab({ prompts }: { prompts: Prompt[] }) {
  const save = useSave();
  const [draft, setDraft] = useState(prompts);
  useEffect(() => setDraft(prompts), [prompts]);
  const set = (i: number, p: Partial<Prompt>) => setDraft(draft.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <section className="card">
      <div className="card__head"><h2>Daily prompts</h2><span className="small muted">Sent from planner@onlycat.com. In preview, only to the allowlist.</span></div>
      {draft.map((p, i) => (
        <div key={p.id} className="settings-row">
          <label className="switch"><input type="checkbox" checked={p.on} aria-label={`${p.id} on`} onChange={(e) => set(i, { on: e.target.checked })} /><span /></label>
          <span className="settings-row__k"><b style={{ textTransform: "capitalize" }}>{p.id}</b><small>{p.days.join(", ")}</small></span>
          <input className="input input--sm" type="time" aria-label={`${p.id} time`} value={p.at} onChange={(e) => set(i, { at: e.target.value })} />
          <input className="input" style={{ flex: "2 1 240px" }} aria-label={`${p.id} text`} value={p.text} onChange={(e) => set(i, { text: e.target.value })} />
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={JSON.stringify(draft) === JSON.stringify(prompts)} onClick={() => save("the prompts", paths.setting("prompts"), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function BoardTab({ slides }: { slides: Slide[] }) {
  const save = useSave();
  const [draft, setDraft] = useState(slides);
  useEffect(() => setDraft(slides), [slides]);
  const move = (i: number, d: number) => { const n = [...draft]; const [x] = n.splice(i, 1); n.splice(Math.max(0, Math.min(n.length, i + d)), 0, x); setDraft(n); };
  return (
    <section className="card">
      <div className="card__head"><h2>Board slides</h2><span className="small muted">In this order; the week slide is the main one</span></div>
      {draft.map((s, i) => (
        <div key={s.id} className="settings-row">
          <span className="settings-row__k"><b style={{ textTransform: "capitalize" }}>{s.id}</b></span>
          <input className="input input--sm input--num" inputMode="numeric" aria-label={`${s.id} seconds`} value={s.seconds} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, seconds: Number(e.target.value.replace(/\D/g, "") || 0) } : x)))} />
          <span className="small muted">seconds</span>
          <button type="button" className="btn btn--sm" aria-label={`Move ${s.id} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
          <button type="button" className="btn btn--sm" aria-label={`Move ${s.id} down`} disabled={i === draft.length - 1} onClick={() => move(i, 1)}>↓</button>
        </div>
      ))}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-3)" }}>
        <button type="button" className="btn btn--primary" disabled={JSON.stringify(draft) === JSON.stringify(slides)} onClick={() => save("the board slides", paths.setting("board.slides"), { value: draft }, "setting")}>Save</button>
      </div>
    </section>
  );
}

function RolesTab() {
  const people = useApi<PersonRow[]>(paths.people(), ["person"]);
  const save = useSave();
  if (!people.data) return <Loading />;
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>People and roles</h2><span className="small muted">Operators read and log; supervisors plan and count; admins change settings</span></div>
      <table className="table">
        <thead><tr><th>Name</th><th>Email</th><th>Contract</th><th>Prompts</th><th>Role</th></tr></thead>
        <tbody>{people.data.map((p) => (
          <tr key={p.id}><td><b>{p.name}</b>{!p.plannable ? <div className="small muted">not planned</div> : null}</td><td className="small">{p.email}</td>
            <td className="small">{p.contractText ?? "—"}</td><td>{p.notify ? "yes" : "no"}</td>
            <td><select className="select select--inline" aria-label={`${p.name}'s role`} value={p.role} onChange={(e) => save(`${p.name}'s role`, paths.person(p.id), { role: e.target.value as Role }, "person")}>
              <option value="operator">operator</option><option value="supervisor">supervisor</option><option value="admin">admin</option>
            </select></td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}
